import { FC, ReactElement, HTMLAttributes } from 'react';
import { makeClass } from '../../utils';

export const Appearance = {
  inline: 'inline',
  normal: 'normal',
  vibrant: 'vibrant'
} as const;

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  /**
   * Determines the appearance of the spinner
   * `inline`   inherit from text color
   * `normal`   single color
   * `vibrant`  three color
   */
  appearance?: string | keyof typeof Appearance;
  /**
   * Determines the label
   */
  label?: string;
  /**
   * Determines the position of the label
   *
   * Note: if using a small spinner, the label can only be positioned on the right.
   */
  labelPosition?: 'bottom' | 'right';
  /**
   * Adjusts the size of the spinner
   */
  size?: 'medium' | 'small';
  /**
   * Set if spinner is unavailable
   */
  unavailable?: boolean;
}

const CIRCLE_DEPTHS = {
  [Appearance.inline]: 2,
  [Appearance.normal]: 2,
  [Appearance.vibrant]: 4
};

const Circle = ({ index }: { index: number }) => (
  <svg className={`circle circle-${index}`} viewBox='0 0 28 28'>
    <circle
      fill='none'
      strokeWidth='4'
      strokeLinecap='round'
      cx='14'
      cy='14'
      r='12'
    />
  </svg>
);

/**
 * The `<Spinner />` component extends `HTMLAttributes<HTMLDivElement>`.
 * @param props
 * @constructor
 * @example
 * <Spinner appearance='vibrant' labelPosition='bottom' label='Loading' />
 * @version 2.1
 */
const Spinner: FC<SpinnerProps> = (props: SpinnerProps): ReactElement => {
  const {
    size,
    appearance,
    label,
    labelPosition,
    unavailable,
    className,
    ...rest
  } = props;
  const classNamesList = makeClass([
    className,
    'j-spinner',
    unavailable && `j-spinner-unavailable`,
    `j-spinner-appearance__${appearance}`,
    `j-spinner-size__${size}`,
    size !== 'small' && labelPosition === 'bottom'
      ? `j-spinner-has-label-bottom`
      : `j-spinner-has-label-right`
  ]);

  if (unavailable) {
    return (
      <div className={classNamesList} {...rest}>
        Unavailable
      </div>
    );
  }

  return (
    <div className={classNamesList} {...rest}>
      <div className='j-spinner-circles'>
        {[...Array(CIRCLE_DEPTHS[appearance || Appearance.normal])].map(
          (_v, index) => (
            <Circle key={index} index={index} />
          )
        )}
      </div>
      {label && <label>{label}</label>}
    </div>
  );
};

Spinner.displayName = 'Spinner';
Spinner.defaultProps = {
  appearance: Appearance.normal,
  size: 'medium',
  labelPosition: 'bottom',
  unavailable: false
};

export default Spinner;
